/* globals aptrinsic,ga */
/* @flow */
import { FETCH_SUCCESS as FETCH_PROFILE_SUCCESS } from '../profile/action-types.js';
import { profileEmail as getProfileEmail } from '../profile/getters.js';
import { SAVE_SUCCESS as SAVE_USER_SUCCESS } from '../users/action-types.js';
import { Observable } from 'rxjs';
import { combineEpics, type ActionsObservable } from 'redux-observable';
import createDebugger from 'debug';
const debug = createDebugger('app:modules:tracking'); // eslint-disable-line no-unused-vars
import { prop, tryCatch, pipe, map, join, path } from 'ramda';
import { FETCH_REPORTS_SUCCESS, FETCH_IOCS_SUCCESS } from 'modules/search/action-types.js';
import { STORE_FAILURE as STORE_REPORT_FAILURE } from 'modules/reports/action-types.js';

import {
  REGISTER_APM_ENDPOINT,
  APM_EVENT_TYPE,
  marketplaceApmParams,
} from './constants.js';
import {
  NODE_CLICKED,
  FETCH_SUCCESS as FETCH_CONSTELLATION_SUCCESS,
} from '../constellation/action-types.js';
import { REGISTER_ACTIVITY as REGISTER_MARKETPLACE_ACTIVITY } from '../marketplace/action-types.js';
import {PREMIUM_CONTENT_LOCK_CLICK, UPDATE_PREFERENCES_FAILURE} from '../profile/action-types';

const newrelic = window.newrelic;

const getRateLimitByProfile = tryCatch(
  // $FlowIgnore: prop can take a number
  pipe(prop('company'), prop('requestQuotas'), prop(0), prop('maxRequests')),
  () => -1
);

const getSharingGroupsByProfile = tryCatch(
  pipe(prop('company'), prop('sharingGroups'), map(prop('name')), join(', ')),
  () => null
);

const profileTrackingEpic = (action$: ActionsObservable<*>) =>
  action$.ofType(FETCH_PROFILE_SUCCESS)
    .do(({ payload }) => {
      debug('Initializing Aptrinsic for user:', payload);
      try {
        const trackedUser = {
          id: payload.guid,
          email: payload.email,
          isTrialCompany: payload.company.trialCompany,
          rateLimit: getRateLimitByProfile(payload),
          enclaveInboxLimit: payload.company.maxIngestEmailsCount,
          firstVisitDate: payload.activationDate,
        };
        const trackedAccount = {
          id: payload.company.guid,
          name: payload.company.name,
        };

        // $FlowIgnore: browser global, nothing to worry about here
        aptrinsic('identify', trackedUser, trackedAccount);
        debug('Initialized Aptrinsic user and account', trackedUser, trackedAccount);
      } catch (err) {
        debug('Aptrinsic user could not be initialized. Adblocker?', payload, err);
      }
    })
    .do(({ payload }) => {
      debug('Initializing New Relic for user:', payload);
      try {
        newrelic.setCustomAttribute('userGuid', payload.guid);

        debug('Initialized New Relic user and account', payload.email);
      } catch (err) {
        debug('New Relic user could not be initialized. Adblocker?', payload, err);
      }
    })
    .ignoreElements();


const searchTrackingEpic = action$ =>
  Observable.zip(
    action$.ofType(FETCH_REPORTS_SUCCESS),
    action$.ofType(FETCH_IOCS_SUCCESS)
  )

    // $FlowIgnore: Ramda + Rxjs types are not cooperating...
    .map(map(pipe(prop('payload'), prop('data'))))
    .do(x => {
      const [reports, iocs] = x;
      let total = 0;

      try {
        total = reports.size + iocs.size;
      } catch (err) {
        debug('Error calculating total ioc and report results from search', err);
      } finally {
        // $FlowIgnore: browser global, nothing to worry about here
        aptrinsic('track', 'Search Results', {'Results Count': total});
      }
    })
    .ignoreElements();

/**
 * Track "hover" events (we're actually tracking mouseover events). Any element
 * with data-selector will be tracked.
 *
 * NOTE: Ideally this would also track mouseout so that we only fire an event if
 * the mouse remains over an element for some threshold of time.
 */
const hoverEventEpic = (action$: ActionsObservable<*>) =>
  Observable.defer(() => Observable.fromEvent(document.body, 'mouseover'))
    .filter(e => e.target && e.target.dataset.selector) // Only capture events where the el has [data-selector] set
    .map(prop('target'))
    .debounceTime(200) // Meant to help with noise while moving mouse over multiple elements
    .do(el => {
      try {
        // $FlowIgnore: We're in a try catch...
        const selector = el.dataset.selector;
        const hoverId = `hover:${selector}`;

        // $FlowIgnore: browser global, nothing to worry about here
        aptrinsic('track', 'intent', { intentId: hoverId, date: (new Date()).toISOString() });
        debug('Tracked hover', hoverId);
      } catch (err) {
        debug('Error tracking hover. Adblocker?', err, el);
      }
    })
    .ignoreElements();

/**
 * Captures a click event on IOC from within our constellation graphs or the IOC page in explore.
 */
const iocClickedEpic = (action$) => {
  const iocNodeClick = action$.ofType(NODE_CLICKED)
    .map(prop('payload'))
    .filter(nodeData => !!nodeData && !!nodeData.type && !!nodeData.value)
    .map(node => ({
      value: node.value,
      type: node.type,
      priorityLevel: node.priorityLevel,
      clickSource:'Constellation graph'
    }));

  const reportOrIocOpened = action$.ofType(FETCH_CONSTELLATION_SUCCESS)
    .map(path(['payload', 'data', 'nodes']))
    .map(nodes => nodes.find(node => node.rootNode))
    .filter(nodeData => !!nodeData && !!nodeData.type && !!nodeData.value)
    .map(node => ({
      value: node.value,
      type: node.type,
      priorityLevel: node.priorityLevel,
      clickSource:'IOC Explore Table or Search'
    }));

  return Observable.merge(
    iocNodeClick,
    reportOrIocOpened
  )
  .do(clickData => {
    // $FlowIgnore: browser global, nothing to worry about here
    aptrinsic('track', 'Indicator Clicked', {'Node Data': clickData});
  })
  .ignoreElements()
  .catch((err, source) => {
    debug('Error tracking high priority node click', err);
    return Observable.throw(err);
  }); };


/* Register the action of clicking on an IOC on the APM endpoint */
const apmIndicatorActivityEpic = (action$, store, { ajax }) => {

  const iocNodeClicked = action$.ofType(NODE_CLICKED)
    .map(prop('payload'))
    .distinctUntilChanged((x,y) => x.guid === y.guid)
    .filter(payload => !!payload && !!payload.type && !!payload.value)
    .map(node => ({
      eventType: APM_EVENT_TYPE,
      value: node.value,
      type: node.type,
      priorityLevel: node.priorityLevel,
      clickSource:'Constellation graph'
    }));

  const reportOrIocOpened = action$.ofType(FETCH_CONSTELLATION_SUCCESS)
    .map(path(['payload', 'data', 'nodes']))
    .map(nodes => nodes.find(node => node.rootNode))
    .filter(nodes => !!nodes && !!nodes.type && !!nodes.value)
    .map(node => ({
      eventType: APM_EVENT_TYPE,
      value: node.value,
      type: node.type,
      priorityLevel: node.priorityLevel,
      clickSource:'IOC Explore Table or Search'
    }));

  return Observable
    .merge(iocNodeClicked, reportOrIocOpened)
    .distinctUntilChanged((x,y) => x.value === y.value)
    .throttleTime(500)
    .mergeMap((nodeData) => {
      return ajax.postJSON(REGISTER_APM_ENDPOINT, nodeData)
        .ignoreElements()
        .catch((err, source) => {
          debug('Error tracking indicator node click', err);
          return Observable.throw(err);
        });
    });
};

/* Records the marketplace query on the BE */
const apmMarketplaceActivityEpic = (action$, store, { ajax }) =>
  action$.ofType(REGISTER_MARKETPLACE_ACTIVITY)
    .mergeMap(({ payload }) => {
      return ajax
        .postJSON(REGISTER_APM_ENDPOINT, marketplaceApmParams)
        .ignoreElements()
        .catch((err, source) => {
          debug('Error tracking marketplace tab click', err);
          return Observable.throw(err);
        });
    });


const reportFailureEpic = (action$) =>
  action$.ofType(STORE_REPORT_FAILURE)
    .throttleTime(200) // See NOTE
    .map(prop('payload'))
    .map(prop('request'))
    .map(req => ({
      status: req.status,
      req: `${req.method} ${req.url}`,
    }))
    .do(x => {
      // $FlowIgnore: browser global, nothing to worry about here
      aptrinsic('track', 'Fetch Report Failure', x);
    })
    .ignoreElements()
    .catch((err, source) => {
      debug('Error tracking report fetch failure', err);
      return Observable.throw(err);
    });

// Disabled until we want to handle locked content differently
const lockedContentClickEpic = (action$) =>
  action$.ofType(PREMIUM_CONTENT_LOCK_CLICK)
    .map(({payload}) => {
      // $FlowIgnore: browser global, nothing to worry about here
      aptrinsic('track', 'Premium locked content clicked', {payload});
      return Observable.empty();
    })
    .catch(err => {
      debug('Error tracking locked content click', err);
      return Observable.throw(err);
    });

export const epic = combineEpics(
  searchTrackingEpic,
  profileTrackingEpic,
  hoverEventEpic,
  iocClickedEpic,
  reportFailureEpic,
  apmIndicatorActivityEpic,
  apmMarketplaceActivityEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/tracking/index.js